// ...existing code...
import React from "react";
import { motion } from "framer-motion";
import {
  FaStethoscope,
  FaCloud,
  FaHeartbeat,
  FaShieldAlt,
  FaUserMd,
  FaRegClock,
  FaNotesMedical,
} from "react-icons/fa";
import "./Healthcare.css";
import { Link } from "react-router-dom";

const FEATURES = [
  {
    icon: <FaStethoscope />,
    title: "Telemedicine Platforms",
    text: "Secure video consultations, scheduling and remote triage to improve patient access and continuity of care.",
  },
  {
    icon: <FaNotesMedical />,
    title: "EHR & Workflow Integration",
    text: "End-to-end integrations that centralize records, simplify clinician workflows and reduce administrative overhead.",
  },
  {
    icon: <FaHeartbeat />,
    title: "AI-assisted Diagnostics",
    text: "Machine learning models assist image analysis and predictive insights to accelerate diagnoses and reduce errors.",
  },
  {
    icon: <FaCloud />,
    title: "Cloud-native Infrastructure",
    text: "Scalable, resilient cloud solutions with strong security and cost-optimized operations.",
  },
  {
    icon: <FaShieldAlt />,
    title: "Compliance & Security",
    text: "Design and engineering aligned with HIPAA, GDPR and industry best practices for patient data protection.",
  },
  {
    icon: <FaRegClock />,
    title: "Rapid, Iterative Delivery",
    text: "Outcome-focused agile delivery with measurable value at every milestone.",
  },
];

const WHY = [
  {
    icon: <FaShieldAlt />,
    title: "Regulatory-first",
    text: "We embed compliance in architecture and delivery, not as an afterthought.",
  },
  {
    icon: <FaUserMd />,
    title: "Clinical Expertise",
    text: "Cross-functional teams including clinicians ensure practical, safe solutions.",
  },
  {
    icon: <FaCloud />,
    title: "Proven Scalability",
    text: "Cloud practices that grow with patient volumes and analytics needs.",
  },
];

const Card = ({ icon, title, text, i }) => (
  <motion.article
    className="hc-card"
    initial={{ opacity: 0, y: 18, scale: 0.995 }}
    whileInView={{ opacity: 1, y: 0, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: i * 0.08 }}
    aria-labelledby={`hc-card-${i}-title`}
  >
    <div className="hc-icon" aria-hidden>
      {icon}
    </div>
    <div className="hc-card-body">
      <h3 id={`hc-card-${i}-title`}>{title}</h3>
      <p>{text}</p>
    </div>
  </motion.article>
);

const Healthcare = () => {
  return (
    <main className="healthcare-container">
      {/* Header without external image - decorative gradient + shapes via CSS */}
       <header className="healthcare-header">
             <div className="header-content">
               <h1>HEALTCHCARE</h1>
               <p className="subtitle">
              Driving digital healthcare innovation with advanced solutions for patient management, telemedicine, and medical data analytics—improving care delivery and operational excellence.
               </p>
     
               <div className="header-ctas">
                  <Link
               to="/contact" // ✅ Navigate to Contact page
               className="btn-primary"
               aria-label="Contact Lyros"
             >
               Talk to our Expert team
             </Link>
               </div>
             </div>
     
             <svg className="header-decor" viewBox="0 0 800 200" preserveAspectRatio="none" aria-hidden="true">
               <defs>
                 <linearGradient id="gB" x1="0" x2="1">
                   <stop offset="0" stopColor="#0ea5e9" stopOpacity="0.14" />
                   <stop offset="1" stopColor="#6366f1" stopOpacity="0.06" />
                 </linearGradient>
               </defs>
               <path d="M0 140 C160 40 360 240 800 80 L800 200 L0 200 Z" fill="url(#gB)"></path>
             </svg>
           </header>
     
      <section className="container hc-intro">
        <motion.p
          className="section-desc"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
         The healthcare industry is dedicated to improving the quality of life by providing medical care, advanced treatments, and preventive health solutions. With the growing demand for patient-centric care, innovative technologies, and reliable health management systems, healthcare organizations play a vital role in ensuring accessibility, affordability, and efficiency in medical services.</motion.p>
      </section>

      <section className="container hc-features">
        <h2 className="section-title">Our Approach</h2>
        <div className="hc-cards">
          {FEATURES.map((f, idx) => (
            <Card key={idx} icon={f.icon} title={f.title} text={f.text} i={idx} />
          ))}
        </div>
      </section>

      <section className="container hc-examples">
        <h2 className="section-title"> Our Healthcare Services</h2>
        <div className="hc-examples-grid">
          <motion.div
            className="example-card"
            initial={{ opacity: 0, x: -12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h4>Hospital Management Solutions</h4>
            <p>Streamlined digital platforms that improve hospital administration, appointment scheduling, billing, and patient record management for seamless healthcare delivery.</p>
          </motion.div>

          <motion.div
            className="example-card"
            initial={{ opacity: 0, x: 12 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.08 }}
          >
            <h4>Telemedicine & Remote Care</h4>
            <p>Empowering patients to connect with doctors anytime, anywhere, through secure virtual consultations and digital health monitoring.</p>
          </motion.div>

          <motion.div
            className="example-card"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.16 }}
          >
            <h4>Insurance & Claims Management</h4>
            <p>Digitized insurance platforms that simplify claims processing, policy management, and ensure transparency for patients and insurers.</p>
          </motion.div>
        </div>
      </section>

      <section className="container hc-why">
        <h2 className="section-title">Why Choose Lyros</h2>
        <div className="hc-why-grid">
          {WHY.map((w, i) => (
            <motion.div
              className="why-card"
              key={i}
              initial={{ opacity: 0, scale: 0.98 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
            >
              <div className="why-icon" aria-hidden>
                {w.icon}
              </div>
              <div className="why-content">
                <h4>{w.title}</h4>
                <p>{w.text}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Healthcare;
// ...existing code...